import { useCallback, useEffect, useState } from "react";

// The wallboard's liveness mechanism (spec-3 dec-1): a plain client-side poll.
// Backstage is a separate process and cannot tap the core app's in-process SSE /
// mutate() bus, so there is deliberately NO real-time push here (no LISTEN/NOTIFY,
// no SSE/WebSocket) — just a refetch on an interval (ac-8/ac-11/ac-12).
//
// `fetcher` MUST be stable (memoise it with useCallback). The hook refetches
// immediately whenever the fetcher identity changes — that's what makes a query
// change (e.g. toggling hide-internal) refresh at once instead of on the next tick.
export const POLL_INTERVAL_MS = 10_000;

export interface PollResult<T> {
  data: T | null;
  error: boolean;
  refetch: () => Promise<void>;
}

export function usePoll<T>(
  fetcher: () => Promise<T>,
  intervalMs: number = POLL_INTERVAL_MS,
): PollResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState(false);

  const refetch = useCallback(async () => {
    try {
      setData(await fetcher());
      setError(false);
    } catch {
      setError(true);
    }
  }, [fetcher]);

  useEffect(() => {
    void refetch();
    const id = setInterval(() => void refetch(), intervalMs);
    return () => clearInterval(id);
  }, [refetch, intervalMs]);

  return { data, error, refetch };
}
